import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../../utils';
import { isForLoadedAssessmentPage, markAssessmentPageChanged } from './utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3State } from '../../../types';
import type {
  AssessmentMutationAction,
  CreateTreatmentPlanV3AssessmentMethodPayload,
  DeleteTreatmentPlanV3AssessmentMethodPayload,
  ITreatmentPlanV3AssessmentMethod,
  TreatmentPlanV3AssessmentThunks,
  UpdateTreatmentPlanV3AssessmentMethodPayload,
} from '../types';

export function addCreateTreatmentPlanV3AssessmentMethodCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { createTreatmentPlanV3AssessmentMethod } = thunks;
  const flag = 'createAssessmentMethodLoading';

  builder
    .addCase(createTreatmentPlanV3AssessmentMethod.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3AssessmentMethod.fulfilled,
      (
        state,
        action: AssessmentMutationAction<
          ITreatmentPlanV3AssessmentMethod,
          CreateTreatmentPlanV3AssessmentMethodPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action) || !state.assessmentPage) return;
        markAssessmentPageChanged(state);
        state.assessmentPage.methods.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3AssessmentMethod.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

export function addUpdateTreatmentPlanV3AssessmentMethodCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { updateTreatmentPlanV3AssessmentMethod } = thunks;
  const flag = 'updateAssessmentMethodLoading';

  builder
    .addCase(updateTreatmentPlanV3AssessmentMethod.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3AssessmentMethod.fulfilled,
      (
        state,
        action: AssessmentMutationAction<
          ITreatmentPlanV3AssessmentMethod,
          UpdateTreatmentPlanV3AssessmentMethodPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action)) return;
        const list = state.assessmentPage?.methods;
        if (!list) return;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index === -1) return;
        markAssessmentPageChanged(state);
        list[index] = { ...action.payload, visuals: list[index].visuals };
      },
    )
    .addCase(updateTreatmentPlanV3AssessmentMethod.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

export function addDeleteTreatmentPlanV3AssessmentMethodCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { deleteTreatmentPlanV3AssessmentMethod } = thunks;
  const flag = 'deleteAssessmentMethodLoading';

  builder
    .addCase(deleteTreatmentPlanV3AssessmentMethod.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3AssessmentMethod.fulfilled,
      (
        state,
        action: AssessmentMutationAction<void, DeleteTreatmentPlanV3AssessmentMethodPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action) || !state.assessmentPage) return;
        markAssessmentPageChanged(state);
        state.assessmentPage.methods = state.assessmentPage.methods.filter(
          (row) => row.id !== action.meta.arg.methodId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3AssessmentMethod.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
